import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection!: signalR.HubConnection;
  private isConnectionStarted = false;
  private receiveMessageCallback?: (user: string, message: string) => void;

  constructor() {}

  startConnection() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/chatHub`, {
        accessTokenFactory: async () => {
          try {
            const session:any = await fetchAuthSession();
            return session.tokens.idToken;
          } catch (err) {
            console.error('Error getting token:', err);
            return '';
          }
        },
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information) // optional: for debug
      .build();

    this.hubConnection
      .start()
      .then(() => {
        // console.log('SignalR connection started');
        this.isConnectionStarted = true;
        this.registerListeners(); // attach listeners
      })
      .catch((err: any) => {
        this.isConnectionStarted = false;
        console.error('Error starting SignalR connection:', err);
      });

    this.hubConnection.onclose(error => {
      this.isConnectionStarted = false;
      console.error('SignalR connection closed:', error);
    });

    this.hubConnection.onreconnecting(error => {
      console.warn('SignalR reconnecting...', error);
    });

    this.hubConnection.onreconnected(() => {
      console.log('SignalR reconnected');
      this.isConnectionStarted = true;
      this.registerListeners(); // Re-attach listeners
    });
  }

  private registerListeners() {
    // Avoid duplicate listeners
    this.hubConnection.off('ReceiveMessage');

    this.hubConnection.on('ReceiveMessage', (user: string, message: string) => {
      console.log('Message received from', user, ':', message);
      if (this.receiveMessageCallback) {
        this.receiveMessageCallback(user, message);
      }
    });
  }

  sendMessage(userA: string, userB: string, message: string) {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      console.warn('Cannot send message: SignalR not connected');
      return;
    }

    this.hubConnection
      .invoke('SendMessageToUser', userA, userB, message)
      .catch((err: any) => console.error('Error sending message:', err));
  }

  onReceiveMessage(callback: (user: string, message: string) => void) {
    this.receiveMessageCallback = callback;
    if (this.isConnectionStarted) {
      this.registerListeners(); // ensure listener is set
    }
  }

  stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop().then(() => {
        console.log('SignalR connection stopped');
        this.isConnectionStarted = false;
      });
    }
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.hubConnection?.state ?? signalR.HubConnectionState.Disconnected;
  }
}
